import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

const INTRO = [
  "These Terms of Service govern your access to and use of PAYD, a milestone-based invoicing and escrow coordination platform operated by PAYD LLC.",
  "By accessing or using PAYD, you agree to these Terms. If you do not agree, do not use the platform.",
];

const SECTIONS = [
  {
    id: "neutral-platform",
    title: "PAYD Is a Neutral Platform",
    blocks: [
      { type: "p", text: "PAYD provides software tools that allow users to create invoices, define milestones, deposit digital assets into smart contracts, and release payments based on user-confirmed completion." },
      { type: "p", text: "PAYD is not a party to any contract, invoice, statement of work, milestone agreement, service relationship, employment relationship, partnership, agency relationship, or transaction between users." },
      { type: "p", text: "All agreements are solely between the client and the service provider." },
    ],
  },
  {
    id: "no-guarantee",
    title: "No Guarantee of Work, Payment, Results, or Performance",
    blocks: [
      { type: "p", text: "PAYD does not guarantee:" },
      { type: "ul", items: [
        "that any user will perform services",
        "that any user will pay",
        "that any milestone will be completed",
        "that any deliverable will meet expectations",
        "that any user is truthful, qualified, solvent, or legally authorized to transact",
        "that any smart contract will function without error",
        "that funds will be recoverable after user error, blockchain error, wallet compromise, or smart contract execution",
      ] },
      { type: "p", text: "Users are solely responsible for reviewing counterparties, invoices, milestone terms, wallet addresses, and release instructions." },
    ],
  },
  {
    id: "blockchain-risk",
    title: "Smart Contract and Blockchain Risk",
    blocks: [
      { type: "p", text: "PAYD may use blockchain networks and autonomous smart contracts to facilitate escrow-style payment flows." },
      { type: "p", text: "You acknowledge and agree that blockchain transactions may be irreversible and that smart contracts may execute automatically according to their code." },
      { type: "p", text: "PAYD is not responsible for losses arising from:" },
      { type: "ul", items: [
        "smart contract bugs, vulnerabilities, exploits, or failures",
        "blockchain congestion, downtime, forks, validator issues, or protocol changes",
        "incorrect wallet addresses",
        "lost private keys",
        "unauthorized wallet access",
        "user mistakes",
        "third-party wallet, bridge, exchange, token, RPC, or infrastructure failures",
        "volatility, depegging, or liquidity issues involving digital assets",
        "regulatory restrictions affecting digital assets or blockchain networks",
      ] },
      { type: "p", text: "You use blockchain features at your own risk." },
    ],
  },
  {
    id: "user-contracts",
    title: "User-to-User Contracts",
    blocks: [
      { type: "p", text: "Any contract, invoice, scope of work, milestone, release condition, revision obligation, refund obligation, or dispute is solely between the users involved." },
      { type: "p", text: "PAYD may display information submitted by users, but PAYD does not verify, endorse, supervise, or guarantee any user agreement." },
    ],
  },
  {
    id: "fees",
    title: "Fees",
    blocks: [
      { type: "p", text: "PAYD may charge platform, transaction, service, or processing fees. Fees may be deducted from payments, charged separately, or reflected in the platform interface." },
      { type: "p", text: "Fees are non-refundable unless PAYD determines otherwise in its sole discretion." },
    ],
  },
  {
    id: "disputes",
    title: "Disputes Between Users",
    blocks: [
      { type: "p", text: "PAYD may provide optional dispute tools, messaging, evidence upload, or release-confirmation workflows." },
      { type: "p", text: "PAYD is not required to resolve disputes between users. If PAYD chooses to assist, such assistance does not make PAYD a party to the underlying transaction." },
      { type: "p", text: "PAYD may freeze, pause, restrict, or delay platform functionality if it suspects fraud, illegal activity, platform abuse, sanctions risk, security risk, or violation of these Terms." },
    ],
  },
  {
    id: "prohibited-uses",
    title: "Prohibited Uses",
    blocks: [
      { type: "p", text: "You may not use PAYD for:" },
      { type: "ul", items: [
        "fraud, deception, money laundering, wash transactions, Sybil activity, fake volume, market manipulation, or grant manipulation",
        "illegal goods or services",
        "sanctions violations",
        "terrorist financing",
        "unauthorized financial services",
        "weapons, drugs, stolen goods, counterfeit goods, adult exploitation, or other restricted activity",
        "impersonation or false identity",
        "harassment, threats, or abusive behavior",
        "exploiting smart contracts, infrastructure, or other users",
      ] },
      { type: "p", text: "PAYD may suspend or terminate accounts at any time for suspected violations." },
    ],
  },
  {
    id: "compliance-taxes",
    title: "Compliance and Taxes",
    blocks: [
      { type: "p", text: "You are solely responsible for complying with all laws applicable to your transactions, services, taxes, reporting, licenses, permits, employment classification, and digital asset activity." },
      { type: "p", text: "PAYD does not provide legal, tax, accounting, investment, financial, employment, or compliance advice." },
    ],
  },
  {
    id: "account-security",
    title: "Account Security",
    blocks: [
      { type: "p", text: "You are responsible for your account, wallet, device, private keys, seed phrases, passwords, and transaction approvals." },
      { type: "p", text: "PAYD is not liable for unauthorized access caused by your failure to secure your account, wallet, or credentials." },
    ],
  },
  {
    id: "no-custody",
    title: "No Custody Unless Expressly Stated",
    blocks: [
      { type: "p", text: "Unless expressly stated in writing, PAYD does not custody user funds. Funds may be held by autonomous smart contracts, third-party providers, or blockchain infrastructure." },
      { type: "p", text: "PAYD does not guarantee recovery of funds once a transaction is submitted or executed." },
    ],
  },
  {
    id: "availability",
    title: "Platform Availability",
    blocks: [
      { type: "p", text: "PAYD may modify, suspend, discontinue, or restrict the platform at any time without liability." },
      { type: "p", text: "PAYD does not guarantee uninterrupted access, error-free operation, or compatibility with any blockchain, wallet, device, browser, token, or third-party service." },
    ],
  },
  {
    id: "ip",
    title: "Intellectual Property",
    blocks: [
      { type: "p", text: "PAYD owns all rights in the PAYD platform, software, branding, interface, documentation, designs, content, and related intellectual property." },
      { type: "p", text: "Users retain ownership of their submitted materials, but grant PAYD a limited license to host, display, transmit, process, and use such materials to operate the platform." },
    ],
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    blocks: [
      { type: "p", text: "To the maximum extent permitted by law, PAYD LLC, its owners, officers, employees, contractors, affiliates, and service providers shall not be liable for indirect, incidental, special, consequential, punitive, exemplary, lost-profit, lost-revenue, lost-data, lost-goodwill, blockchain-related, smart-contract-related, or digital-asset-related damages." },
      { type: "p", text: "To the maximum extent permitted by law, PAYD's total liability for any claim shall not exceed the greater of:" },
      { type: "ul", items: [
        "the fees paid by you to PAYD in the three months before the claim arose; or",
        "$100.",
      ] },
    ],
  },
  {
    id: "indemnification",
    title: "Indemnification",
    blocks: [
      { type: "p", text: "You agree to defend, indemnify, and hold harmless PAYD LLC and its affiliates from any claims, losses, damages, liabilities, fees, costs, or expenses arising from:" },
      { type: "ul", items: [
        "your use of PAYD",
        "your transactions with other users",
        "your services, deliverables, invoices, or milestones",
        "your breach of these Terms",
        "your violation of law",
        "your misuse of blockchain, smart contracts, wallets, or digital assets",
        "any dispute between you and another user",
      ] },
    ],
  },
  {
    id: "arbitration",
    title: "Arbitration Agreement and Class Action Waiver",
    blocks: [
      { type: "p", text: "You and PAYD agree that any dispute, claim, or controversy arising out of or relating to these Terms, PAYD, the platform, smart contracts, payments, invoices, milestones, or user transactions shall be resolved exclusively through binding individual arbitration." },
      { type: "p", text: "The arbitration shall take place in New York, New York, unless PAYD elects remote proceedings." },
      { type: "p", text: "You waive the right to bring or participate in any class action, collective action, representative action, mass arbitration, private attorney general action, or jury trial." },
      { type: "p", text: "Claims must be brought only in an individual capacity." },
      { type: "p", text: "No court or arbitrator may consolidate claims or preside over any class, collective, representative, or mass proceeding unless PAYD agrees in writing." },
    ],
  },
  {
    id: "governing-law",
    title: "Governing Law",
    blocks: [
      { type: "p", text: "These Terms are governed by the laws of the State of New York, without regard to conflict-of-law principles." },
      { type: "p", text: "Any court proceeding permitted under these Terms must be brought exclusively in the state or federal courts located in New York County, New York." },
    ],
  },
  {
    id: "termination",
    title: "Termination",
    blocks: [
      { type: "p", text: "PAYD may suspend, restrict, or terminate your access at any time, with or without notice, if PAYD believes you violated these Terms, created risk, caused potential liability, abused the platform, or engaged in suspicious activity." },
    ],
  },
  {
    id: "changes",
    title: "Changes to Terms",
    blocks: [
      { type: "p", text: "PAYD may update these Terms at any time. Continued use of the platform after changes become effective means you accept the updated Terms." },
    ],
  },
  {
    id: "contact",
    title: "Contact",
    blocks: [
      { type: "contact", name: "PAYD LLC", address: "282 Mountain Road, Pleasantville, NY, 10570", email: "hello@payd.day" },
    ],
  },
];

const Terms = () => (
  <LegalLayout title="Terms of Service" lastUpdated="June 28, 2026" intro={INTRO} sections={SECTIONS} />
);

export default Terms;
